import React, { useEffect, useState } from 'react';
import { Segment, Form, Portal, Header, Breadcrumb, Dropdown } from 'semantic-ui-react';
import { DateTimeUtils, DatesRangeInput, NestedDropdown, Notification, STATUS_TYPES, VALIDATORS, generateResolver, yup, Button, productRestriction, PRODUCT_PERMISSIONS, USER_PERMISSIONS } from 'dyl-components';

import { Controller, useController, useForm } from 'react-hook-form';
import SettingsFooter from 'dyl-components/molecules/SettingsFooter';
import useWidthListener from 'shared/SettingsFooter/useWidthListener';
import useWindowWidth from 'shared/SettingsFooter/useWindowWidth';
import { MathUtils, StringUtils } from 'utils';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import campaignCategoryActions from 'actions/campaign_category';
import { FIELD_TYPE, FIELD_TYPE_CONTROL } from 'shared/CustomData/DynamicField';
import campaignsActions from 'actions/campaigns';
import ConfirmModal from 'shared/confirmation/ConfirmModal';
import CustomPrompt from 'shared/confirmation/CustomPrompt';
import { useConfirm } from 'shared/confirmation/useConfirm';
import applyCurrencySchema from 'shared/schemas/products/applyCurrencySchema';
import { Error404, Error500 } from 'pages/ErrorPages';
import RestrictedView from 'shared/RestrictedView';

const CAMPAIGN_CONVERSION_VALUES = ['lead', 'opportunity', 'customer', 'custom'];

const Campaign = ({
    id,

    name,
    shared_type,
    description,
    campaign_conversion,
    campaign_type,

    period,
    category,
    subcategory,

    expected_responses,
    expected_revenue,
    budgeted_cost,
    isOwner
}) => {
    const width = useWidthListener("settingsSidebar");
    const windowWidth = useWindowWidth();

    const dispatch = useDispatch();
    const productPermissions = useSelector(state => state.subscription.productPermissions);
    const hasText = productRestriction(PRODUCT_PERMISSIONS.TEXT_VIEW, productPermissions);

    const { categories, isSaving, isCheckingDuplicate } = useSelector(state => ({
        categories: state.campaign_category.categories.map(category => ({
            key: category.id,
            value: category.id,
            text: category.name,
            options: (
                category?.campaign_subcategories?.map(type => ({
                    key: type.id,
                    value: type.id,
                    text: type.name
                })) || []
            )
        })) || [],
        isSaving: state.campaigns.isCreatingCampaign || state.campaigns.campaignBeingUpdated,
        isCheckingDuplicate: state.campaigns.isCheckingDuplicate
    }))

    const [typingTimeout, setTypingtimeout] = useState(null);

    const NAME_VALIDATOR = yup.string().no_whitespace_only().no_excessive_whitespaces().maxlength(64).simple_alphanumeric();
    const { formState: { isValid, isDirty, dirtyFields }, control, handleSubmit, setError, clearErrors, resetField } = useForm({
        mode: 'onChange',
        defaultValues: {
            shared_type: shared_type || '',
            name: name || '',
            description: description || '',

            period: period || '',
            category: category || null,
            subcategory: subcategory || null,

            expected_responses: expected_responses || null,
            expected_revenue: expected_revenue || null,
            budgeted_cost: budgeted_cost || null,
            campaign_conversion: campaign_conversion,
            campaign_type: campaign_type || ['dialer']
        },
        resolver: generateResolver({
            shared_type: yup.mixed().oneOf(['personal', 'company']).required('This field is required'),
            name: NAME_VALIDATOR.required('This field is required'),
            description: VALIDATORS.DESCRIPTION(),
            campaign_conversion: yup.string().oneOf(CAMPAIGN_CONVERSION_VALUES).required('This field is required'),
            campaign_type: yup.array().of(yup.string().when('shared_type', {
                is: value => value === 'company',
                then: schema => schema.oneOf(['dialer', 'text']),
                otherwise: schema => schema.oneOf(['dialer'])
            }).oneOf(['dialer', 'text'])).min(1, 'Please select at least one outreach type').required('This field is required'),
            period: yup.string().when("shared_type", {
                is: value => value === 'company',
                then: schema => schema.required('This field is required').test('valid_range', 'Please enter valid start date and end date', function (value) {
                    if (!!!value) {
                        return true;
                    }
                    const [start_date, end_date] = value.split(' - ');
                    return DateTimeUtils.isValid(start_date, DateTimeUtils.WORD_DATE_FORMAT) && DateTimeUtils.isValid(end_date, DateTimeUtils.WORD_DATE_FORMAT);
                }).test('cannot_select_past_dates', 'Start date should not be a past date', function (value) {
                    if (!!!value || id) {
                        return true;
                    }
                    const [start_date] = value.split(' - ');
                    const currentDate = DateTimeUtils.getCurrentDate(DateTimeUtils.WORD_DATE_FORMAT);
                    return !DateTimeUtils.isValid(start_date, DateTimeUtils.WORD_DATE_FORMAT) || !DateTimeUtils.dateIsBeforeAnotherDate(start_date, currentDate, "days", DateTimeUtils.WORD_DATE_FORMAT)
                }).test('end_date_cannot_be_before_start_date', 'End date should be later than start date', function (value) {
                    if (!!!value) {
                        return true;
                    }
                    const [start_date, end_date] = value.split(' - ');
                    if (!DateTimeUtils.isValid(start_date, DateTimeUtils.WORD_DATE_FORMAT) || !DateTimeUtils.isValid(end_date, DateTimeUtils.WORD_DATE_FORMAT)) {
                        return true;
                    }
                    return !DateTimeUtils.dateIsBeforeAnotherDate(end_date, start_date, "days", DateTimeUtils.WORD_DATE_FORMAT);
                })
            }),
            expected_responses: yup.lazy(value => value === null || value === '' ? yup.string().nullable(true) : yup.number().min(0, "Expected responses must be at least 0%").max(100, "Expected responses cannot exceed 100%")),
            expected_revenue: yup.lazy(value => value === null || value === '' ? yup.string().nullable(true) : applyCurrencySchema(yup.number(), "Please enter a valid revenue", 0)),
            budgeted_cost: yup.lazy(value => value === null || value === '' ? yup.string().nullable(true) : applyCurrencySchema(yup.number(), "Please enter a valid cost", 0))
        })
    });

    const navigate = useNavigate();

    const onSave = async (data) => {
        try {
            const { name, description, shared_type, campaign_conversion } = data;
            const payload = {
                name: dirtyFields.name ? name : undefined,
                description: dirtyFields.description ? description : undefined,
                campaign_conversion,
                ...(shared_type === 'company' ? (() => {
                    const { period, category: campaign_category_id, subcategory: campaign_subcategory_id, expected_responses, expected_revenue, budgeted_cost } = data;
                    const [start_time, end_time] = period.split(' - ');
                    return {
                        has_company: {
                            campaign_category_id: dirtyFields.category ? campaign_category_id : undefined,
                            campaign_subcategory_id: dirtyFields.subcategory ? campaign_subcategory_id : undefined,
                            expected_responses: dirtyFields.expected_responses ? (expected_responses === '' ? 0 : expected_responses) : undefined,
                            expected_revenue: dirtyFields.expected_revenue ? (expected_revenue === '' ? 0 : expected_revenue) : undefined,
                            budgeted_cost: dirtyFields.budgeted_cost ? (budgeted_cost === '' ? 0 : budgeted_cost) : undefined,
                            start_time: dirtyFields.period ? DateTimeUtils.getUnixTime(start_time, DateTimeUtils.WORD_DATE_FORMAT) : undefined,
                            end_time: dirtyFields.period ? DateTimeUtils.getUnixTime(end_time, DateTimeUtils.WORD_DATE_FORMAT) : undefined,
                            campaign_type: dirtyFields.campaign_type ? data.campaign_type : undefined
                        }
                    }
                })() : {})
            }
            await (id ?
                dispatch(campaignsActions.updateCampaign(Number(id), payload)) :
                dispatch(campaignsActions.createCampaign(payload))
            );
            Notification.alert(`Successfully ${id ? 'updated' : 'created'} ${shared_type} campaign!`, STATUS_TYPES.SUCCESS);
            navigate('/campaigns', { replace: true, state: { saved: true } });
        } catch (e) {
            console.log(e);
            if (e?.Code !== 409) {
                Notification.alert(`Failed to ${id ? 'update' : 'create'} ${shared_type} campaign`, STATUS_TYPES.ERROR);
            } else {
                Notification.alert(`Campaign name already exists!`, STATUS_TYPES.ERROR);
                setError('name', {
                    type: 'unique_campaign',
                    message: 'Campaign name already exists!'
                });
            }
        }
    }

    const campaignConversionField = (
        <Controller
            control={control}
            name="campaign_conversion"
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                <Form.Select
                    label='Campaign Conversion'
                    disabled={id !== undefined}
                    required
                    value={value}
                    onChange={(_, { value }) => {
                        onChange({ target: { name, value } })
                    }}
                    error={error?.message}
                    placeholder='Select Campaign Conversion'
                    options={CAMPAIGN_CONVERSION_VALUES.map(value => ({
                        key: value,
                        value,
                        text: StringUtils.capitalize(value)
                    }))}
                    selectOnBlur={false}
                />
            )}
        />
    )

    const { field: sharedTypeField, fieldState: sharedTypeFieldState } = useController({
        control,
        name: "shared_type"
    })

    const isCompanyCampaign = sharedTypeField.value === 'company';

    const outreachTypeField = (
        <Controller 
            control={control}
            name="campaign_type"
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                <Form.Select
                    label='Outreach Type'
                    required
                    value={value}
                    removeIcon={isCompanyCampaign ? undefined : null}
                    onChange={(_, { value }) => {
                        if (!isCompanyCampaign) {
                            onChange({ target: { name, value: ['dialer'] } })
                            return;
                        }
                        onChange({ target: { name, value } })
                    }}
                    error={error?.message}
                    placeholder='Select Outreach Type'
                    options={[
                        {
                            key: 'dialer', value: 'dialer', text: 'Dialer'
                        },
                        {
                            key: 'text', value: 'text', text: 'Text'
                        }
                    ].filter(option => {
                        return option.value === 'dialer' || (hasText && isCompanyCampaign);
                    })}
                    selectOnBlur={false}
                    multiple
                    readOnly={!isCompanyCampaign}
                    renderLabel={(label) => ({
                        color: 'ToggableDropdown__label',
                        content: label.text,
                        ...(isCompanyCampaign ? {} : { onRemove: null })
                    })}
                />
            )}
        />
    )

    const { isConfirmed } = useConfirm();

    CustomPrompt(null, isDirty, isConfirmed, 'Changes not saved', 'Are you sure you want to exit?');

    const SHARED_DROPDOWN = [
        { key: 'personal', value: 'personal', text: 'Personal', userPermission: USER_PERMISSIONS.CAMPAIGNS_ADD},
        { key: 'company', value: 'company', text: 'Company', userPermission: USER_PERMISSIONS.CAMPAIGNS_ADD },
    ]

    return (
        <>
            <ConfirmModal />
            <Segment basic disabled={isSaving} loading={isSaving}>
                <Breadcrumb>
                    <Breadcrumb.Section as={Link} to='/campaigns'>
                        Campaigns
                    </Breadcrumb.Section>
                    <Breadcrumb.Divider />
                    <Breadcrumb.Section>
                        {id ? `Edit "${name}"` : "Create Campaign"}
                    </Breadcrumb.Section>
                </Breadcrumb>
                <Header as='h1' color='primary'>
                    {id ? 'Edit' : 'Create'} Campaign
                </Header>
                <Form noValidate>
                    <Form.Group widths={4}>
                        <Form.Select
                            label='Shared Type'
                            disabled={id !== undefined}
                            required
                            value={sharedTypeField.value}
                            text={SHARED_DROPDOWN.find(item => item.value === sharedTypeField.value)?.text}
                            error={sharedTypeFieldState?.error?.message}
                            placeholder='Select Shared Type'
                            selectOnBlur={false}
                        >
                            <Dropdown.Menu>
                                {SHARED_DROPDOWN.map(item => (
                                    <RestrictedView type="disabled" userPermission={item.userPermission} userRestrict modifiers={item.value}>
                                        <Dropdown.Item key={item.key} value={item.value} text={item.text} active={sharedTypeField.value === item.value} selected={sharedTypeField.value === item.value} sharedTypeValue={sharedTypeField.value} onClick={() => {
                                            sharedTypeField.onChange({ target: { name: sharedTypeField.name, value: item.value } });
                                            if (item.value === 'personal') {
                                                resetField('expected_responses');
                                                resetField('budgeted_cost');
                                                resetField('expected_revenue');
                                                resetField('period');
                                                resetField('category');
                                                resetField('subcategory');
                                            }
                                        }} />
                                    </RestrictedView>
                                ))}
                            </Dropdown.Menu>
                        </Form.Select>
                        <Controller
                            control={control}
                            name='name'
                            render={({ field: { name: fieldName, value, onChange }, fieldState: { error } }) => (
                                <Form.Input
                                    label='Campaign Name'
                                    required
                                    value={value}
                                    onChange={async (_, { value }) => {
                                        if (typingTimeout) {
                                            clearTimeout(typingTimeout)
                                        }
                                        onChange({ target: { name: fieldName, value } });
                                        const isValid = await NAME_VALIDATOR.isValid(value);
                                        if (isValid && value !== '') {
                                            setTypingtimeout(setTimeout(async () => {
                                                await clearErrors(fieldName);
                                                if (!id || value !== name) {
                                                    const duplicate = (await dispatch(campaignsActions.checkDuplicate({ campaign_name: value })))?.id;
                                                    if (duplicate) {
                                                        await setError("name", {
                                                            type: 'unique_campaign',
                                                            message: 'Campaign name already exists!'
                                                        })
                                                    }
                                                }
                                            }, 150));
                                        }
                                    }}
                                    error={error?.message}
                                    loading={isCheckingDuplicate}
                                    placeholder='Enter Campaign Name'
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name='description'
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <Form.Input
                                    label='Description'
                                    value={value}
                                    onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                    error={error?.message}
                                    placeholder='Enter Description'
                                />
                            )}
                        />
                        {!isCompanyCampaign && campaignConversionField}
                        {isCompanyCampaign && (
                            <Controller
                                control={control}
                                name='period'
                                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                    <Form.Field
                                        control={DatesRangeInput}
                                        label='Start/End Date'
                                        value={value}
                                        onChange={(_, { value }) => {
                                            const newValue = (() => {
                                                if (id) {
                                                    const [start_date] = period.split(' - ');
                                                    const [, end_date] = value?.split(' - ');
                                                    if (end_date) {
                                                        return `${start_date} - ${end_date}`;
                                                    }
                                                    return `${start_date} - `;
                                                }
                                                return value;
                                            })();
                                            onChange({ target: { name, value: newValue } })
                                        }}
                                        error={error?.message}
                                        placeholder='Select Start/End Date'
                                        clearable
                                        required
                                        dateFormat={DateTimeUtils.WORD_DATE_FORMAT}
                                        minDate={DateTimeUtils.getCurrentDate(DateTimeUtils.WORD_DATE_FORMAT)}
                                    />
                                )}
                            />
                        )}
                    </Form.Group>
                    <Form.Group widths={4}>
                        {isCompanyCampaign && (
                            <>
                                <Controller
                                    name='category'
                                    control={control}
                                    render={({ field: { name: parentName, value: parentValue, onChange: parentOnChange } }) => (
                                        <Controller
                                            name='subcategory'
                                            control={control}
                                            render={({ field: { name, value, onChange } }) => (
                                                <Form.Field>
                                                    <label>Campaign Category/Subcategory</label>
                                                    <NestedDropdown
                                                        allowedToSelectParentOnly
                                                        child_value={value}
                                                        parent_value={parentValue}
                                                        loading={false}
                                                        nested_options={categories}
                                                        onChange={(_, { parent_value, child_value }) => {
                                                            parentOnChange({ target: { name: parentName, value: parent_value } });
                                                            onChange({ target: { name, value: child_value } });
                                                        }}
                                                        placeholder={`Select Category/Subcategory`}
                                                        display_parent
                                                        selection
                                                    />
                                                </Form.Field>
                                            )}
                                        />
                                    )}
                                />
                                {campaignConversionField}
                            </>
                        )}
                        {outreachTypeField}
                    </Form.Group>
                    {isCompanyCampaign && [
                        <Header as='h2' color='primary'>
                            Expected Campaign Success
                        </Header>,
                        <Form.Group widths={4}>
                            <Controller
                                control={control}
                                name='expected_responses'
                                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                    <Form.Field
                                        control={FIELD_TYPE_CONTROL[FIELD_TYPE.PERCENT]}
                                        label='Expected Responses'
                                        value={value}
                                        onChange={(_, { value }) => { onChange({ target: { name, value: value !== '' && value != null ? StringUtils.formatDecimal(String(value)) : value } }) }}
                                        error={error?.message}
                                        placeholder='Enter Expected Responses'
                                        min='0'
                                        max='100'
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name='expected_revenue'
                                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                    <Form.Field
                                        control={FIELD_TYPE_CONTROL[FIELD_TYPE.CURRENCY]}
                                        label='Expected Revenue'
                                        value={value}
                                        onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                        error={error?.message}
                                        placeholder='Enter Expected Revenue'
                                        type='number'
                                        min='0'
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name='budgeted_cost'
                                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                    <Form.Field
                                        control={FIELD_TYPE_CONTROL[FIELD_TYPE.CURRENCY]}
                                        label='Budgeted Cost'
                                        value={value}
                                        onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                        error={error?.message}
                                        placeholder='Enter Budgeted Cost'
                                        type='number'
                                        min='0'
                                    />
                                )}
                            />
                        </Form.Group>
                    ]}
                </Form>
            </Segment>
            <Portal open>
                <SettingsFooter
                    style={{ width: MathUtils.calculatePercentage(windowWidth, windowWidth - width) }}
                    className={`Webform__menu`}
                    rightOptions={[
                        <Button as={Link} disabled={isSaving} basic status='cancel' to='/campaigns'>Cancel</Button>,
                        <Button loading={isSaving} onClick={handleSubmit(onSave)} disabled={!isValid || !isDirty || isSaving || isCheckingDuplicate || isOwner} primary>
                            Save
                        </Button>
                    ]}
                />
            </Portal>
        </>
    );
};

const CampaignContainer = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    useEffect(() => {
        if (id) {
            dispatch(campaignsActions.readCampaign(id));
        }
        dispatch(campaignCategoryActions.getCampaignCategoryOptions())
    }, [id, dispatch]);

    const {
        name,
        shared_type,
        description,
        period,
        category,
        subcategory,
        campaign_conversion,
        campaign_type,

        expected_responses,
        expected_revenue,
        budgeted_cost,

        isReadingCampaign,
        campaignError,
        isOwner,
    } = useSelector(state => {
        const { name, description, is_company_campaign: campaignDetails, campaign_conversion, user_owner_id } = state.campaigns.campaign || {
            name: '',
            description: '',
            campaign_conversion: null,
            campaign_type: null,
            is_company_campaign: null
        }
        const isCompanyCampaign = campaignDetails?.start_time;
        const start_date = isCompanyCampaign ? DateTimeUtils.changeFormat(DateTimeUtils.convertUnixTimeToDate(campaignDetails.start_time), "", DateTimeUtils.WORD_DATE_FORMAT, 'UTC') : '';
        const end_date = isCompanyCampaign ? DateTimeUtils.changeFormat(DateTimeUtils.convertUnixTimeToDate(campaignDetails.end_time), "", DateTimeUtils.WORD_DATE_FORMAT, 'UTC') : '';
        const period = isCompanyCampaign ? `${start_date} - ${end_date}` : '';
        return ({
            isReadingCampaign: state.campaigns.isReadingCampaign,

            shared_type: isCompanyCampaign ? 'company' : 'personal',
            name: name || '',
            description: description || '',

            period,
            category: campaignDetails?.campaign_category?.campaign_category_id || null,
            subcategory: campaignDetails?.campaign_category?.campaign_subcategory_id || null,

            expected_responses: campaignDetails?.expected_responses || null,
            expected_revenue: campaignDetails?.expected_revenue || null,
            budgeted_cost: campaignDetails?.budgeted_cost || null,
            campaign_conversion: campaign_conversion || null,
            isOwner: user_owner_id !== state.auth.user_id,
            campaign_type: campaignDetails?.campaign_type || null,

            campaignError: state.campaigns.campaignError
        });
    });

    if (isReadingCampaign) {
        return <Segment loading basic />
    }

    if (!id) {
        return (
            <RestrictedView type="restricted" userPermission={USER_PERMISSIONS.CAMPAIGNS_ADD}>
                <Campaign />
            </RestrictedView>
        )
    }
    
    if (!isReadingCampaign && campaignError) {
        return <Error500 message={"Something went wrong"} />;
    }

    if (!isReadingCampaign && !name) {
        return <Error404 message={"Campaign not found"} />;
    }
    
    if (!isReadingCampaign && campaignError) {
        return <Error500 message={"Something went wrong"} />;
    }

    if (!isReadingCampaign && !name) {
        return <Error404 message={"Campaign not found"} />;
    }

    return (
        <RestrictedView type="restricted" userPermission={USER_PERMISSIONS.CAMPAIGNS_EDIT}>
            <Campaign
                budgeted_cost={budgeted_cost}
                category={category}
                subcategory={subcategory}
                shared_type={shared_type}
                description={description}
                expected_responses={expected_responses}
                expected_revenue={expected_revenue}
                id={id}
                name={name}
                period={period}
                campaign_conversion={campaign_conversion}
            campaign_type={campaign_type}
                isOwner={isOwner}
            />
        </RestrictedView>

    )
}

export default CampaignContainer;
